
import React from 'react';
import { SectionTitle } from './SectionTitle';
import { ScrollAnimation } from './ScrollAnimation';
import { ConversationIcon } from './icons/ConversationIcon';
import { PresentationChartIcon } from './icons/PresentationChartIcon';
import { PianoKeysIcon } from './icons/PianoKeysIcon';
import { useTranslation } from '../i18n/LanguageContext';

const icons = {
    conversation: ConversationIcon,
    presentation: PresentationChartIcon,
    piano: PianoKeysIcon,
};

export const ExperimentalClassInfoSection: React.FC = () => {
  const { t } = useTranslation();
  const steps = t('experimentalClass.steps') as { key: keyof typeof icons; title: string; description: string; isHighlight?: boolean }[];

  return (
    <section id="aula-experimental-info" className="py-16 md:py-32 overflow-hidden bg-brand-secondary/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimation animation="slideInUp">
          <SectionTitle
            title={t('experimentalClass.title')}
            subtitle={t('experimentalClass.subtitle')}
          />
        </ScrollAnimation>
        
        <div className="relative max-w-3xl mx-auto mt-16 md:mt-24">
          {/* Linha Vertical - Centralizada nos 56px da coluna de ícones (56 / 2 = 28px -> left-7) */}
          <div className="absolute left-7 top-0 bottom-0 w-1 bg-slate-800/40 rounded-full" aria-hidden="true" />
          
          {/* Linha de Progresso (Até o passo 2) */}
          <div className="absolute left-7 top-0 w-1 h-[66%] bg-gradient-to-b from-gradient-start to-gradient-end rounded-full opacity-40 shadow-[0_0_10px_rgba(34,211,238,0.2)]" aria-hidden="true" />

          <ul className="space-y-12 md:space-y-16 relative">
            {steps.map((step, index) => {
              const Icon = icons[step.key];
              return (
                <li key={index} className="grid grid-cols-[56px_1fr] gap-x-6 md:gap-x-10 items-start">
                  
                  {/* Coluna 1: Ícone (Largura Fixa) */}
                  <div className="relative flex justify-center z-10">
                    <ScrollAnimation animation="scaleIn" delay={100 + index * 100} threshold={0.5}>
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-500
                        ${step.isHighlight 
                          ? 'bg-gradient-to-br from-gradient-start to-gradient-end shadow-[0_0_25px_rgba(34,211,238,0.5)] scale-110 border-2 border-white/20' 
                          : 'bg-brand-primary border border-slate-700 shadow-xl'}`}>
                        <Icon className={`w-7 h-7 ${step.isHighlight ? 'text-white' : 'text-brand-accent'}`} />
                      </div>
                    </ScrollAnimation>
                  </div>

                  {/* Coluna 2: Conteúdo */}
                  <div className="pt-1">
                    <ScrollAnimation animation="slideInUp" delay={200 + index * 100} threshold={0.2}>
                      <div className={`p-6 md:p-8 rounded-2xl border transition-all duration-500
                        ${step.isHighlight 
                          ? 'bg-brand-primary/90 border-brand-accent/40 shadow-2xl' 
                          : 'bg-brand-primary/40 border-slate-800/60 hover:border-slate-700'}`}>
                        
                        <div className="flex flex-col">
                          <span className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-brand-accent/70 mb-2">
                            {t('experimentalClass.title').includes('First') || t('experimentalClass.title').includes('Primeira') ? `Step 0${index + 1}` : `Passo 0${index + 1}`}
                          </span>
                          <h3 className="text-xl md:text-2xl font-serif font-bold text-brand-light mb-3 leading-tight">
                            {step.title}
                          </h3>
                          <p className="text-brand-text text-sm md:text-base leading-relaxed opacity-90">
                            {step.description}
                          </p>
                        </div>

                        {step.isHighlight && (
                          <div className="mt-5 flex items-center gap-2 text-brand-accent text-xs font-bold tracking-wide uppercase">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-accent opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-accent"></span>
                            </span>
                            {t('guidedMelody.status.userTurn')}
                          </div>
                        )}
                      </div>
                    </ScrollAnimation>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};
